/**
 * studio-shop D4 — the shop grid (`shop-grid`): every product the studio shows,
 * each with its picture, name and lowest prices; a product opens in the page's
 * product window (the `shop-product` piece the page places). A studio that
 * shows nothing draws nothing. shop-self-fulfilment D5: a product with none of
 * any option left is marked sold out for a site's own drawing to say.
 * shop-self-fulfilment (Gabriel 2026-10-31): placed with `ships`, it shows only
 * the products the studio ships herself, or only the others, and its window
 * steps through those alone.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'shop-grid',
  description: "The shop's products in a grid, each with its picture, name and lowest prices; a product opens in the page's product window.",
  placeable: true,
  options: {
    ships: {
      type: 'enum',
      values: ['all', 'service', 'studio'],
      default: 'all',
      description:
        "Which products it shows: every one, only those a fulfilment service makes and ships, or only those the studio added and ships herself. The window it opens steps through the same ones.",
    },
  },
  parts: {
    grid: { description: 'The whole grid.', required: false },
    tile: { description: "One product; opens it in the page's product window.", required: true },
    image: { description: "The product's picture.", required: false, role: 'photo' },
    title: { description: "The product's name.", required: true, role: 'title' },
    price: { description: 'Its lowest price in each format: "Paper from $60 · Canvas from $350".', required: true },
  },
  actions: {
    open: {
      description: "Opens one product in the page's product window, by its handle.",
      args: [{ name: 'handle', kind: 'item', list: 'products', key: 'handle' }],
    },
  },
  events: {},
});

/** One option of a product (a format and size), so a drawing can show a print at a size it is sold in. */
export interface ShopGridOption {
  readonly optionId: string;
  /** The format as the catalog names it ("paper", "canvas"); null for options with no format. */
  readonly format: string | null;
  /** The size: "18 × 24 in"; null when the option has none. */
  readonly size: string | null;
  /** As the bag names it: "Paper 18 × 24 in". */
  readonly label: string;
  /** Its price: "$95.00". */
  readonly price: string;
  /** Its picture, in this size's shape: a small copy about 480 px wide; null when it has none of its own (draw the product's). */
  readonly image: { readonly url: string; readonly alt: string } | null;
}

/** One product in the grid. */
export interface ShopGridProduct {
  readonly handle: string;
  readonly title: string;
  /** Its picture: a small copy about 480 px wide, light enough for a grid or a row; the product window draws the service's own picture. */
  readonly image: { readonly url: string; readonly alt: string } | null;
  /** Its lowest price in each format: "Paper from $60.00 · Canvas from $350.00", or "From $40.00". */
  readonly prices: string;
  /** Its lowest price of all: "$60.00". */
  readonly from: string;
  /** Its highest price of all: "$350.00"; the same as `from` when every option costs the same. */
  readonly to: string;
  /** The options on sale, in the studio's order. */
  readonly options: readonly ShopGridOption[];
  /** None of any of its options are left (shop-self-fulfilment D5). */
  readonly soldOut: boolean;
  /** studio: one she added and ships herself (Shipped by you); service: one a fulfilment service makes and ships. */
  readonly shippedBy: 'service' | 'studio';
}

/** What the grid knows. */
export interface ShopGridState {
  /** loading, ready, or empty (the studio shows no products: nothing is drawn). */
  readonly status: 'loading' | 'ready' | 'empty';
  /** In the studio's order. */
  readonly products: readonly ShopGridProduct[];
}

/** What a drawing can ask the grid to do. */
export interface ShopGridActions {
  /** Opens one product in the page's product window; its previous and next step through this grid. */
  readonly open: (handle: string) => void;
}

/** The grid reports nothing to its host. */
export type ShopGridEvents = Record<string, never>;
